import { type Table } from '@tanstack/react-table';
import type {
  CSSProperties,
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  ReactNode,
  RefObject,
} from 'react';
import { DataTableColumnHeader } from './DataTableHeader.js';
import { getDataTableCellProps, wpPinToCellPin, type DataTableDensity } from './dataTableCell.js';
import { renderDataTableDataRow as DataTableDataRow } from './renderDataTableDataRow.js';
import { useDataTableRowVirtualizer } from './useDataTableRowVirtualizer.js';
import { wpPinSide } from './useDataTableCore.js';
import type { Column, DataTableSelection } from './types.js';
import type { PageSelectionState } from './useDataTableSelection.js';
import styles from './DataTable.module.css';
import { cx } from '../../utils/cx.js';

export type DataTableTableViewProps<T> = {
  tableRef: RefObject<HTMLTableElement | null>;
  wpTable: Table<T>;
  resolvedColumns: Column<T>[];
  rowKey: (row: T) => string;
  dataLength: number;
  totalCols: number;
  effectiveFixedLayout: boolean;
  stickyHScroll: boolean;
  columnResizeEnabled: boolean;
  resizableTableStyle?: CSSProperties;
  colGroupCols: { key: string; widthPct: string }[] | null;
  compactActions: boolean;
  density: DataTableDensity;
  rowLeadingActions?: (row: T) => ReactNode;
  rowActions?: (row: T) => ReactNode;
  actionsHeaderColumn?: Column<T>;
  selection?: DataTableSelection<T>;
  pageSelectableKeys?: readonly string[];
  pageSelectionState?: PageSelectionState;
  onTogglePageSelection?: () => void;
  onToggleRowSelection?: (key: string) => void;
  onRowClick?: (row: T) => void;
  rowProps?: (row: T) => HTMLAttributes<HTMLTableRowElement> | undefined;
  rowTrWrapper?: (
    row: T,
    tr: ReactNode,
    ctx: { rowIndex: number; sortableId: string },
  ) => ReactNode;
  /** Centered empty message inside the structural tbody row. */
  emptyState?: ReactNode;
  /** When true, tbody rows virtualize inside `scrollParentRef` (large scrollable lists). */
  virtualizeRows?: boolean;
  scrollParentRef?: RefObject<HTMLElement | null>;
  showQuickCreateRow: boolean;
  quickCreateCells?: ReactNode[];
  quickCreateCollapsed?: ReactNode;
  quickCreateExpanded?: boolean;
  mergeCellStyles: (
    columnId: string,
    declarativeWidth?: string,
    isHeader?: boolean,
  ) => CSSProperties | undefined;
  resizingKey: string | null;
  onResizePointerDown: (
    columnId: string,
    declarativeWidth: string | undefined,
    e: React.PointerEvent<HTMLButtonElement>,
  ) => void;
  resetColumnWidth: (columnId: string, declarativeWidth?: string) => void;
  rowHScrollDrag: boolean;
  rowHScrollTbodyProps: HTMLAttributes<HTMLTableSectionElement>;
  suppressRowClickRef: MutableRefObject<boolean>;
  receivingSkeleton?: boolean;
  animateReceive?: boolean;
};

/**
 * Truncated cells reveal full value via native tooltip — delegated so per-cell
 * wiring is unnecessary. Only sets `title` when content actually overflows.
 */
function applyTruncationTitle(e: ReactMouseEvent<HTMLTableElement>): void {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const td = target.closest<HTMLTableCellElement>('td[data-wp-datatable-part="body"]');
  if (!td) return;
  const probe = td.firstElementChild instanceof HTMLElement ? td.firstElementChild : null;
  const overflowing =
    td.scrollWidth > td.clientWidth + 1 ||
    (probe !== null && probe.scrollWidth > probe.clientWidth + 1);
  if (overflowing) {
    const text = (td.textContent ?? '').trim();
    if (text) {
      td.title = text;
      td.dataset.wpDatatableTruncTitle = '';
    }
  } else if (td.dataset.wpDatatableTruncTitle !== undefined) {
    td.removeAttribute('title');
    delete td.dataset.wpDatatableTruncTitle;
  }
}

export function DataTableTableView<T>({
  tableRef,
  wpTable,
  resolvedColumns,
  rowKey,
  dataLength,
  totalCols,
  effectiveFixedLayout,
  stickyHScroll,
  columnResizeEnabled,
  resizableTableStyle,
  colGroupCols,
  compactActions,
  density,
  rowLeadingActions,
  rowActions,
  actionsHeaderColumn,
  selection,
  pageSelectableKeys,
  pageSelectionState = 'none',
  onTogglePageSelection,
  onToggleRowSelection,
  onRowClick,
  rowProps,
  rowTrWrapper,
  virtualizeRows = false,
  scrollParentRef,
  showQuickCreateRow,
  quickCreateCells,
  quickCreateCollapsed,
  quickCreateExpanded,
  mergeCellStyles,
  resizingKey,
  onResizePointerDown,
  resetColumnWidth,
  rowHScrollDrag,
  rowHScrollTbodyProps,
  suppressRowClickRef,
  emptyState,
}: DataTableTableViewProps<T>) {
  const headerGroup = wpTable.getHeaderGroups()[0];
  const firstDataColumnKey = resolvedColumns[0]?.key;
  const pageKeys = pageSelectableKeys ?? [];
  const visibleLeafColumns = wpTable.getVisibleLeafColumns();
  const dataRows = wpTable.getRowModel().rows;
  const rowVirtualizer = useDataTableRowVirtualizer({
    enabled: virtualizeRows && dataLength > 0,
    rowCount: dataRows.length,
    scrollParentRef: scrollParentRef ?? { current: null },
    density,
  });
  const virtualRows = virtualizeRows ? rowVirtualizer.getVirtualItems() : null;
  const useVirtualRender = Boolean(virtualizeRows && virtualRows && virtualRows.length > 0);
  const totalVirtualSize = virtualizeRows ? rowVirtualizer.getTotalSize() : 0;
  const paddingTop = virtualRows && virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows && virtualRows.length > 0
      ? totalVirtualSize - virtualRows[virtualRows.length - 1].end
      : 0;

  const renderRow = (tanRow: (typeof dataRows)[number], rowIndex: number, virtualRowStyle?: CSSProperties) => (
    <DataTableDataRow
      key={tanRow.id}
      tanRow={tanRow}
      rowIndex={rowIndex}
      density={density}
      compactActions={compactActions}
      rowLeadingActions={rowLeadingActions}
      rowActions={rowActions}
      selection={selection}
      firstDataColumnKey={firstDataColumnKey}
      onToggleRowSelection={onToggleRowSelection}
      onRowClick={onRowClick}
      rowProps={rowProps}
      rowTrWrapper={rowTrWrapper}
      rowKey={rowKey}
      mergeCellStyles={mergeCellStyles}
      suppressRowClickRef={suppressRowClickRef}
      virtualRowStyle={virtualRowStyle}
      measureRef={useVirtualRender ? rowVirtualizer.measureElement : undefined}
    />
  );

  return (
    <table
      ref={tableRef}
      className={cx(
        styles.table,
        effectiveFixedLayout && styles.tableFixed,
        stickyHScroll && styles.tableStickyScroll,
        columnResizeEnabled && styles.tableResizableColumns,
      )}
      style={resizableTableStyle}
      onMouseOver={applyTruncationTitle}
    >
      {colGroupCols ? (
        <colgroup>
          {colGroupCols.map((col) => (
            <col key={col.key} style={{ width: col.widthPct }} />
          ))}
        </colgroup>
      ) : null}
      <thead className={styles.thead}>
        <tr>
          {headerGroup
            ? headerGroup.headers.map((header) => {
                const colId = header.column.id;
                if (colId === '__actions') {
                  return (
                    <DataTableColumnHeader
                      key={header.id}
                      column={
                        actionsHeaderColumn ?? {
                          key: '__actions',
                          label: '',
                        }
                      }
                      actionColumn
                      compactAction={compactActions}
                      stickySide={wpPinSide(header.column.getIsPinned())}
                      columnWidthStyle={mergeCellStyles('__actions', undefined, true)}
                      resizable={false}
                    />
                  );
                }
                const wpCol = (header.column.columnDef.meta as { wpColumn?: Column<T> } | undefined)?.wpColumn;
                if (!wpCol) return null;
                return (
                  <DataTableColumnHeader
                    key={header.id}
                    column={wpCol}
                    stickySide={wpPinSide(header.column.getIsPinned())}
                    columnWidthStyle={mergeCellStyles(colId, wpCol.width, true)}
                    resizable={columnResizeEnabled}
                    resizing={resizingKey === colId}
                    onResizePointerDown={(e) => onResizePointerDown(colId, wpCol.width, e)}
                    onResizeDoubleClick={() => resetColumnWidth(colId, wpCol.width)}
                    pageSelection={
                      selection && colId === firstDataColumnKey
                        ? {
                            checked: pageSelectionState === 'all',
                            indeterminate: pageSelectionState === 'some',
                            disabled: pageKeys.length === 0,
                            onChange: () => onTogglePageSelection?.(),
                          }
                        : undefined
                    }
                  />
                );
              })
            : null}
        </tr>
      </thead>
      <tbody className={rowHScrollDrag ? styles.tbodyRowHScroll : undefined} {...rowHScrollTbodyProps}>
        {showQuickCreateRow ? (
          <tr
            data-wp-datatable-row="quick-create"
            className={cx(
              styles.tr,
              styles.trQuickCreate,
              (!quickCreateCollapsed || quickCreateExpanded === true) && styles.trQuickCreateFields,
            )}
          >
            {visibleLeafColumns.map((leafCol, leafIndex) => {
              const colId = leafCol.id;
              const pin = wpPinSide(leafCol.getIsPinned());
              const colIndex = resolvedColumns.findIndex((c) => c.key === colId);
              if (colId === '__actions') {
                const qcAction = getDataTableCellProps({
                  element: 'td',
                  part: 'actions',
                  pin: wpPinToCellPin(pin),
                  density,
                });
                return (
                  <td
                    key={`qc-${colId}`}
                    {...qcAction}
                    style={{ ...mergeCellStyles('__actions'), ...qcAction.style }}
                  >
                    {quickCreateExpanded === false ? null : (quickCreateCells![resolvedColumns.length] ?? null)}
                  </td>
                );
              }
              const wpCol = resolvedColumns[colIndex];
              if (!wpCol) return null;
              const qcBody = getDataTableCellProps({
                element: 'td',
                part: 'body',
                pin: wpPinToCellPin(pin),
                extraClassName: cx(
                  leafIndex === 0 &&
                    quickCreateExpanded === false &&
                    Boolean(quickCreateCollapsed) &&
                    styles.tdQuickCreateCollapsed,
                  wpCol.columnClassName,
                ),
              });
              return (
                <td
                  key={`qc-${colId}`}
                  {...qcBody}
                  style={{ ...mergeCellStyles(colId, wpCol.width), ...(wpCol.columnStyle ?? {}) }}
                >
                  {quickCreateExpanded === false
                    ? leafIndex === 0
                      ? (quickCreateCollapsed ?? null)
                      : null
                    : (quickCreateCells![colIndex] ?? null)}
                </td>
              );
            })}
          </tr>
        ) : null}
        {dataLength === 0 ? (
          <tr className={styles.emptyRow}>
            <td colSpan={totalCols} className={styles.emptyPlaceholder}>
              {emptyState}
            </td>
          </tr>
        ) : useVirtualRender ? (
          <>
            {paddingTop > 0 ? (
              <tr aria-hidden="true" className={styles.trVirtualSpacer}>
                <td colSpan={totalCols} style={{ height: paddingTop }} />
              </tr>
            ) : null}
            {(virtualRows ?? []).map((virtualRow) => {
              const tanRow = dataRows[virtualRow.index];
              if (!tanRow) return null;
              return renderRow(tanRow, virtualRow.index, { height: `${virtualRow.size}px` });
            })}
            {paddingBottom > 0 ? (
              <tr aria-hidden="true" className={styles.trVirtualSpacer}>
                <td colSpan={totalCols} style={{ height: paddingBottom }} />
              </tr>
            ) : null}
          </>
        ) : (
          dataRows.map((tanRow, rowIndex) => renderRow(tanRow, rowIndex))
        )}
      </tbody>
    </table>
  );
}
